/**
 * 视频管理员接口客户端
 *
 * 给管理后台使用的渠道 / 模型 / 系统配置 / 任务管理接口。
 * 普通用户不应当 import 本文件，避免无谓打包。
 */

import { authFetch, readData } from './apiBase';
import type { VideoMode, VideoStatus, VideoProviderType } from './videoApi';

// ---------------- 渠道 ----------------

export interface VideoChannelAdminView {
  id: string;
  name: string;
  apiUrl: string;
  apiModelId: string;
  apiKeyMasked: string;
  provider: VideoProviderType;
  providerLabel: string;
  isActive: boolean;
  sortOrder: number;
  costPerCall: number;
  remark: string | null;
  supportedResolutions: string[];
  createdAt: string;
  updatedAt: string;
}

export interface VideoChannelPayload {
  name?: string;
  apiKey?: string;
  apiUrl?: string;
  apiModelId?: string;
  provider?: VideoProviderType;
  isActive?: boolean;
  sortOrder?: number;
  costPerCall?: number;
  remark?: string | null;
}

/** 未被任何模型引用的渠道视图（provider 放宽为 string：可能是不在注册表内的历史 provider） */
export interface VideoChannelOrphanView extends Omit<VideoChannelAdminView, 'provider'> {
  provider: string;
  jobCount: number;
}

export interface VideoChannelPurgeResult {
  deleted: { id: string; name: string }[];
  skipped: { id: string; name: string; reason: string }[];
}

export const videoChannelAdminApi = {
  async list(): Promise<VideoChannelAdminView[]> {
    const response = await authFetch('/video-channels');
    return readData<VideoChannelAdminView[]>(response, '获取视频渠道失败');
  },
  async getKey(id: string): Promise<{ apiKey: string }> {
    const response = await authFetch(`/video-channels/${encodeURIComponent(id)}/key`);
    return readData<{ apiKey: string }>(response, '读取渠道密钥失败');
  },
  async create(body: VideoChannelPayload): Promise<VideoChannelAdminView> {
    const response = await authFetch('/video-channels', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return readData<VideoChannelAdminView>(response, '创建渠道失败');
  },
  async update(id: string, body: VideoChannelPayload): Promise<VideoChannelAdminView> {
    const response = await authFetch(`/video-channels/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return readData<VideoChannelAdminView>(response, '更新渠道失败');
  },
  /** 复制渠道（apiKey 一并复制；name 自动加 "(副本)" 后缀） */
  async duplicate(id: string): Promise<VideoChannelAdminView> {
    const response = await authFetch(
      `/video-channels/${encodeURIComponent(id)}/duplicate`,
      { method: 'POST' }
    );
    return readData<VideoChannelAdminView>(response, '复制渠道失败');
  },
  async remove(id: string): Promise<void> {
    const response = await authFetch(`/video-channels/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || '删除渠道失败');
    }
  },
  /** 未被任何模型引用的渠道（jobCount 为历史任务数，仅提示用） */
  async listOrphans(): Promise<VideoChannelOrphanView[]> {
    const response = await authFetch('/video-channels/orphans');
    return readData<VideoChannelOrphanView[]>(response, '获取未引用渠道失败');
  },
  /** 批量删除未引用渠道；被模型引用的会被跳过并返回原因 */
  async purgeOrphans(ids: string[]): Promise<VideoChannelPurgeResult> {
    const response = await authFetch('/video-channels/orphans/purge', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return readData<VideoChannelPurgeResult>(response, '批量清理未引用渠道失败');
  },
};

// ---------------- 模型 ----------------

export interface ModeCapabilityFlags {
  audio?: boolean;
  audioReference?: boolean;
  videoReference?: boolean;
  cameraMovement?: boolean;
  motionIntensity?: boolean;
}

export type ModeCapabilitiesMap = Partial<Record<string, ModeCapabilityFlags>>;

export interface VideoModelAdminView {
  id: string;
  modelId: string;
  name: string;
  description: string;
  isActive: boolean;
  sortOrder: number;
  billingMode: 'per_second' | 'per_call';
  creditsPerSecond: number;
  creditsPerCall: number;
  /** 开启音频生成时的倍率（1 = 不加价） */
  audioMultiplier: number;
  /** 参考视频输入每秒积分（0 = 不收） */
  creditsPerSecondInput: number;
  /** 每张参考图输入积分（0 = 不收） */
  creditsPerImageInput: number;
  resolutionMultipliers: Record<string, number>;
  disabledResolutions: string[];
  modeMultipliers: Record<string, number>;
  modeCapabilities: ModeCapabilitiesMap;
  defaultDuration: number;
  defaultResolution: string;
  defaultAspectRatio: string;
  defaultChannelId: string | null;
  text2videoChannelId: string | null;
  img2videoChannelId: string | null;
  firstLastFrameChannelId: string | null;
  appendPrompt: string;
  defaultChannel: { id: string; name: string; provider: string; apiModelId: string } | null;
  text2videoChannel: { id: string; name: string; provider: string; apiModelId: string } | null;
  img2videoChannel: { id: string; name: string; provider: string; apiModelId: string } | null;
  firstLastFrameChannel: { id: string; name: string; provider: string; apiModelId: string } | null;
  createdAt: string;
  updatedAt: string;
}

export type VideoModelPayload = Partial<
  Omit<VideoModelAdminView, 'id' | 'createdAt' | 'updatedAt' | 'defaultChannel' | 'text2videoChannel' | 'img2videoChannel' | 'firstLastFrameChannel'>
>;

export const videoModelAdminApi = {
  async list(): Promise<VideoModelAdminView[]> {
    const response = await authFetch('/video-models');
    return readData<VideoModelAdminView[]>(response, '获取视频模型失败');
  },
  async create(body: VideoModelPayload): Promise<VideoModelAdminView> {
    const response = await authFetch('/video-models', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return readData<VideoModelAdminView>(response, '创建模型失败');
  },
  async update(id: string, body: VideoModelPayload): Promise<VideoModelAdminView> {
    const response = await authFetch(`/video-models/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return readData<VideoModelAdminView>(response, '更新模型失败');
  },
  async remove(id: string): Promise<void> {
    const response = await authFetch(`/video-models/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || '删除模型失败');
    }
  },
};

// ---------------- 视频 Provider / Adapter 管理 ----------------

export const videoProviderAdminApi = {
  /**
   * 切换 provider 是否被隐藏
   *
   * 隐藏只影响：
   * - 「新建渠道」时的下拉选项
   * - 管理后台 Adapter 管理页徽章
   *
   * 不影响：已经使用该 provider 的渠道继续生效。
   */
  async setHidden(providerId: string, isHidden: boolean): Promise<void> {
    const response = await authFetch(
      `/video-models/providers/${encodeURIComponent(providerId)}/state`,
      {
        method: 'PATCH',
        body: JSON.stringify({ isHidden }),
      }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || '更新 Adapter 状态失败');
    }
  },
};

// ---------------- 视频系统配置 ----------------

export interface VideoSystemConfigView {
  maxConcurrentPerUser: number;
  maxJobsPerSubmit: number;
  taskTimeoutSeconds: number;
  workerIntervalMs: number;
  pollUpstreamIntervalMs: number;
  downloadRetryCount: number;
  downloadRetryDelayMs: number;
  orphanCleanupEnabled: boolean;
  orphanCleanIntervalMs: number;
  orphanRetentionMs: number;
  defaultModelId: string | null;
  updatedAt: string;
}

export const videoSystemConfigApi = {
  async get(): Promise<VideoSystemConfigView> {
    const response = await authFetch('/video-system-config');
    return readData<VideoSystemConfigView>(response, '获取视频系统配置失败');
  },
  async update(payload: Partial<Omit<VideoSystemConfigView, 'updatedAt'>>): Promise<VideoSystemConfigView> {
    const response = await authFetch('/video-system-config', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return readData<VideoSystemConfigView>(response, '更新视频系统配置失败');
  },
};

// ---------------- 管理员任务列表 ----------------

export interface VideoJobAdminView {
  id: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  prompt: string;
  model: string;
  mode: VideoMode;
  status: VideoStatus;
  source: string;
  duration: number;
  resolution: string;
  aspectRatio: string;
  cameraMovement: string | null;
  motionIntensity: string | null;
  enableAudio: boolean;
  referenceImageCount: number;
  hasFirstFrame: boolean;
  hasLastFrame: boolean;
  channelId: string | null;
  upstreamTaskId: string | null;
  creditsUsed: number;
  userFacingErrorMessage: string | null;
  error: unknown;
  resultStorageKey: string | null;
  resultStorageType: string | null;
  coverStorageKey: string | null;
  resultDuration: number | null;
  resultWidth: number | null;
  resultHeight: number | null;
  resultFileSize: number | null;
  resultMimeType: string | null;
  isHidden: boolean;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface VideoJobAdminDetail extends VideoJobAdminView {
  resultUrl: string | null;
  coverUrl: string | null;
  referenceImages: string[];
  firstFrameImage: string | null;
  lastFrameImage: string | null;
  upstreamRawResult: unknown;
}

export interface ListAdminVideosParams {
  page?: number;
  pageSize?: number;
  status?: VideoStatus;
  source?: string;
  userEmail?: string;
  model?: string;
  mode?: VideoMode;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface VideoAdminListResponse {
  data: VideoJobAdminView[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export const videoAdminApi = {
  async list(params: ListAdminVideosParams = {}): Promise<VideoAdminListResponse> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    const response = await authFetch(`/admin/videos?${query.toString()}`);
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error((data && data.error) || '获取视频任务列表失败');
    return {
      data: data?.data || [],
      pagination: data?.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 0 },
    };
  },
  async detail(id: string): Promise<VideoJobAdminDetail> {
    const response = await authFetch(`/admin/videos/${encodeURIComponent(id)}`);
    return readData<VideoJobAdminDetail>(response, '获取任务详情失败');
  },
  async deleteMany(ids: string[]): Promise<{ hiddenCount: number }> {
    const response = await authFetch('/admin/videos', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
    return readData<{ hiddenCount: number }>(response, '删除任务失败');
  },
};

// ---------------- MinIO 存储配置 ----------------

export interface StorageConfigView {
  minioEndpoint: string;
  minioPort: number | null;
  minioUseSSL: boolean;
  minioAccessKey: string;
  minioSecretKeyMasked: string;
  minioBucket: string;
  minioPublicDomain: string;
  minioIsPrivate: boolean;
  minioSignedUrlExpiresIn: number;
  updatedAt: string;
}

export interface StorageConfigPayload {
  minioEndpoint?: string | null;
  minioPort?: number | null;
  minioUseSSL?: boolean;
  minioAccessKey?: string | null;
  minioSecretKey?: string | null;
  minioBucket?: string | null;
  minioPublicDomain?: string | null;
  minioIsPrivate?: boolean;
  minioSignedUrlExpiresIn?: number;
}

export const storageConfigAdminApi = {
  async get(): Promise<StorageConfigView> {
    const response = await authFetch('/storage/config');
    return readData<StorageConfigView>(response, '获取 MinIO 配置失败');
  },
  async update(body: StorageConfigPayload): Promise<StorageConfigView> {
    const response = await authFetch('/storage/config', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return readData<StorageConfigView>(response, '更新 MinIO 配置失败');
  },
  async test(body: StorageConfigPayload): Promise<{ passed: boolean; message: string }> {
    const response = await authFetch('/storage/config/test', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return readData<{ passed: boolean; message: string }>(response, 'MinIO 连接测试失败');
  },
};
